import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'jkapf:favorites';
const EVENT_NAME = 'jkapf:favorites-changed';

function readFavorites(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item): item is string => typeof item === 'string');
  } catch {
    return [];
  }
}

function writeFavorites(ids: string[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // ignore quota errors
  }
  window.dispatchEvent(new Event(EVENT_NAME));
}

export function useFavorites() {
  const [ids, setIds] = useState<string[]>(() => readFavorites());

  useEffect(() => {
    const sync = () => setIds(readFavorites());
    window.addEventListener(EVENT_NAME, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(EVENT_NAME, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const toggle = useCallback((id: string) => {
    const current = readFavorites();
    const next = current.includes(id) ? current.filter((item) => item !== id) : [id, ...current];
    writeFavorites(next);
    setIds(next);
    return next.includes(id);
  }, []);

  const isFavorite = useCallback((id: string) => ids.includes(id), [ids]);

  return { ids, count: ids.length, toggle, isFavorite };
}